import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
    SelectableRoleAssignment,
    SetActiveRoleAssignmentResponse,
} from '@pillar-portal/api-types';
import { authApi } from '@/lib/pillarApi';
import { buildRequiredTotpSetupPath } from '@/lib/requiredTotpSetup';
import {
    clearReactQueryAfterPartialRoleSwitch,
    resetReactQueryAfterRoleSwitch,
    setSelectedRoleAssignmentInCache,
} from '@/lib/effectiveSessionRefresh';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { useAuthStore } from '@/store/auth';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

export { resetReactQueryAfterRoleSwitch };

export function getPostRoleSwitchNavigationPath(
    response: SetActiveRoleAssignmentResponse,
    currentPathname: string
): string | null {
    if (response.activation_state === 'legal_acceptance_required') {
        return '/legal-workflow';
    }
    if (response.activation_state === 'mfa_setup_required') {
        return buildRequiredTotpSetupPath(response.mfa_setup_activation_target);
    }
    if (response.activation_state === 'mfa_challenge_required') {
        const searchParams = new URLSearchParams();
        if (response.available_mfa_methods.length > 0) {
            searchParams.set('mfa_methods', response.available_mfa_methods.join(','));
        }
        const query = searchParams.toString();
        return `/login/totp${query.length === 0 ? '' : `?${query}`}`;
    }
    if (
        currentPathname === '/legal-workflow' ||
        currentPathname === '/login/mfa-setup' ||
        currentPathname === '/login/totp'
    ) {
        return '/dashboard';
    }
    return null;
}

export function OrganizationRoleSwitcher() {
    const log = createComponentLogger('OrganizationRoleSwitcher');
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const setActiveRoleAssignmentId = useAuthStore((state) => state.setActiveRoleAssignmentId);
    const roleAssignmentsQuery = useQuery({
        queryKey: ['selectableRoleAssignments'],
        queryFn: authApi.listSelectableRoleAssignments,
        retry: false,
        staleTime: 0,
    });
    const setActiveRoleAssignmentMutation = useMutation({
        mutationFn: authApi.setActiveRoleAssignment,
        onSuccess: (response) => {
            const nextPath = getPostRoleSwitchNavigationPath(response, location.pathname);
            setSelectedRoleAssignmentInCache(queryClient, response.role_assignment_uuid);
            setActiveRoleAssignmentId(null);
            if (response.activation_state !== 'operational') {
                clearReactQueryAfterPartialRoleSwitch(queryClient, log);
                if (nextPath !== null) {
                    runInBackground(navigate(nextPath), (error) => {
                        log.error('Error navigating after role switch', error);
                    });
                }
                return;
            }
            if (nextPath !== null) {
                runInBackground(navigate(nextPath), (error) => {
                    log.error('Error navigating after role switch', error);
                });
            }
            resetReactQueryAfterRoleSwitch(queryClient, log);
        },
    });

    if (roleAssignmentsQuery.isError) {
        return (
            <p role="alert" className="text-xs text-red-700">
                Your available roles could not be loaded. Refresh the page or sign in again.
            </p>
        );
    }

    const roleAssignments: SelectableRoleAssignment[] =
        roleAssignmentsQuery.data?.role_assignments ?? [];
    if (roleAssignments.length < 2) {
        return null;
    }
    const activeRoleAssignment: SelectableRoleAssignment | null =
        roleAssignments.find(
            (roleAssignment) =>
                roleAssignment.role_assignment_uuid ===
                roleAssignmentsQuery.data?.selected_role_assignment_uuid
        ) ?? null;
    const formatRoleAssignment = (roleAssignment: SelectableRoleAssignment): string =>
        roleAssignment.organization_uuid === null
            ? `${roleAssignment.role} (internal)`
            : `${roleAssignment.role} @ ${roleAssignment.organization_name}`;

    return (
        <Select
            value={activeRoleAssignment?.role_assignment_uuid ?? ''}
            onValueChange={(roleAssignmentUuid) => {
                setActiveRoleAssignmentMutation.mutate(roleAssignmentUuid);
            }}
            disabled={setActiveRoleAssignmentMutation.isPending}
        >
            <SelectTrigger className="h-auto py-1 px-2 text-xs bg-gray-50 border-gray-200">
                <SelectValue placeholder="Select role">
                    {activeRoleAssignment === null ? (
                        <span className="text-gray-500">Select role</span>
                    ) : (
                        <div className="text-left">
                            <div className="font-medium text-gray-900 truncate">
                                {activeRoleAssignment.organization_name ?? 'Internal'}
                            </div>
                            <div className="text-gray-500 truncate">
                                {activeRoleAssignment.role}
                            </div>
                        </div>
                    )}
                </SelectValue>
            </SelectTrigger>
            <SelectContent>
                {roleAssignments.map((roleAssignment) => (
                    <SelectItem
                        key={roleAssignment.role_assignment_uuid}
                        value={roleAssignment.role_assignment_uuid}
                    >
                        {formatRoleAssignment(roleAssignment)}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
